import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Upload, Clipboard, Sparkles, Wand2, Plus, Trash2, Download, RotateCcw, Film, Video, Image as ImageIcon, Type, Lock, Zap, ArrowRight, ScanText } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";

const RATIO_BOX = { "16:9": "aspect-video", "9:16": "aspect-[9/16]", "1:1": "aspect-square", "4:5": "aspect-[4/5]", "4:3": "aspect-[4/3]", "3:4": "aspect-[3/4]" };
const ASSIST_ICON = { alternatives: Sparkles };

let TID = 0;
const newText = (o = {}) => ({ tid: ++TID, text: "New text", font: "Anton", size: 72, color: "#FFFFFF", align: "center", y: 0.5, stroke: true, strokeColor: "#000000", animation: "fade", start: 0.2, ...o });

export default function AnimateVideo() {
  const BE = process.env.REACT_APP_BACKEND_URL;
  const [cfg, setCfg] = useState(null);
  const [gate, setGate] = useState(null);
  const [step, setStep] = useState("start"); // start | workflow
  const [startMode, setStartMode] = useState("reference");

  const [referenceMedia, setReferenceMedia] = useState(null); // {storagePath,url,mediaType,w,h,duration}
  const [sourceMedia, setSourceMedia] = useState(null);
  const [creativeDirection, setCreativeDirection] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [motion, setMotion] = useState({ effect_id: "ken_burns", intensity: 55, direction: "right", fit: "cover" });
  const [videoFit, setVideoFit] = useState("cover");
  const [bgColor, setBgColor] = useState("#080808");
  const [duration, setDuration] = useState(6);
  const [quality, setQuality] = useState("medium");
  const [texts, setTexts] = useState([]);
  const [ocr, setOcr] = useState(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [alts, setAlts] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const [engine, setEngine] = useState("native");
  const [aiStage, setAiStage] = useState("");
  const refInput = useRef(null);
  const srcInput = useRef(null);
  const [pending, setPending] = useState({ role: "reference", type: "image" });

  useEffect(() => {
    api.avConfig().then((c) => {
      setCfg(c);
      setStartMode(c.default_start_mode || "reference");
      const def = (c.aspect_ratios || []).find((a) => a.default) || (c.aspect_ratios || [])[0];
      if (def) setAspect(def.id);
      setQuality(c.rendering?.default_quality || "medium");
    }).catch((e) => {
      const st = e?.response?.status;
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Animate Video is not available.";
      setGate({ reason: st === 402 ? "upgrade_required" : "forbidden", message: typeof msg === "string" ? msg : "Not available" });
    });
  }, []);

  const uploadFile = useCallback(async (file, role) => {
    if (!file) return;
    const mediaType = file.type.startsWith("video") ? "video" : "image";
    const r = new FileReader();
    r.onload = async () => {
      try {
        const up = await api.avUpload(r.result, role, mediaType);
        const item = { storagePath: up.storage_path, url: `${BE}${up.url}`, mediaType, w: up.width, h: up.height, duration: up.duration, base64: r.result };
        if (role === "reference") {
          setReferenceMedia(item);
          toast.success("Reference added — analyzing…");
          if (cfg?.start?.auto_analyze && cfg?.features?.ai) analyze(item);
          if (step === "start") setStep("workflow");
        } else {
          setSourceMedia(item);
          toast.success("Source media added");
          if (mediaType === "image" && up.width && up.height) {
            setAspect(up.width >= up.height ? (up.width / up.height > 1.5 ? "16:9" : "4:3") : (up.height / up.width > 1.5 ? "9:16" : "4:5"));
          }
        }
      } catch (e) {
        const st = e?.response?.status;
        toast.error(st === 402 ? "That media type requires Pro." : (e?.response?.data?.detail || "Upload failed"));
      }
    };
    r.readAsDataURL(file);
  }, [BE, cfg, step]); // eslint-disable-line

  const analyze = async (item) => {
    if (!item || !cfg?.features?.ai) return;
    setAnalyzing(true);
    try {
      const res = await api.avAnalyzeReference(item.storagePath, item.mediaType);
      if (res.prompt) setCreativeDirection(res.prompt);
      if (res.aspect_ratio && RATIO_BOX[res.aspect_ratio]) setAspect(res.aspect_ratio);
      toast.success("Creative direction generated — edit it freely");
    } catch (e) {
      toast.message(e?.response?.data?.detail || "Couldn't analyze — write the direction yourself.");
    }
    setAnalyzing(false);
  };

  const runAssistant = async (action) => {
    if (!cfg?.features?.ai_assistant) return;
    setAiBusy(true); setAlts([]);
    try {
      const res = await api.avAssistant(action, creativeDirection, { aspect_ratio: aspect, source_kind: sourceMedia?.mediaType, ocr_text: ocr ? ocr.map((b) => b.text).join(", ") : undefined });
      if (res.text) setCreativeDirection(res.text);
      if (Array.isArray(res.alternatives) && res.alternatives.length) setAlts(res.alternatives);
      toast.success("Applied");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI Assistant unavailable"); }
    setAiBusy(false);
  };

  const runOcr = async () => {
    const item = sourceMedia?.mediaType === "image" ? sourceMedia : referenceMedia?.mediaType === "image" ? referenceMedia : null;
    if (!item?.base64) { toast.message("Add an image first to detect text."); return; }
    try {
      const r = await api.avOcr(item.base64);
      setOcr((r.blocks || []).map((b, i) => ({ id: i, ...b })));
      if (!(r.blocks || []).length) toast.message("No text detected.");
    } catch (e) { toast.error(e?.response?.data?.detail || "OCR unavailable"); }
  };

  // clipboard paste
  useEffect(() => {
    if (!cfg) return;
    const h = (e) => {
      const items = (e.clipboardData || {}).items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) { uploadFile(f, step === "start" ? "reference" : pending.role); e.preventDefault(); return; }
        }
      }
    };
    window.addEventListener("paste", h);
    return () => window.removeEventListener("paste", h);
  }, [cfg, step, pending, uploadFile]);

  const updText = (tid, patch) => setTexts((ts) => ts.map((t) => t.tid === tid ? { ...t, ...patch } : t));
  const delText = (tid) => setTexts((ts) => ts.filter((t) => t.tid !== tid));

  const doRender = async () => {
    setRendering(true); setResult(null); setAiStage("");
    const kind = sourceMedia ? sourceMedia.mediaType : "none";
    const config = {
      aspect_ratio: aspect, duration,
      creative_direction: creativeDirection,
      source: sourceMedia ? { kind, storage_path: sourceMedia.storagePath } : { kind: "none" },
      background: { type: "solid", color: bgColor },
      motion: kind === "image" ? { effect_id: motion.effect_id, intensity: motion.intensity, direction: motion.direction, fit: motion.fit } : undefined,
      video: kind === "video" ? { fit: videoFit } : undefined,
      texts: texts.map((t) => ({ text: t.text, font: t.font, size: t.size, color: t.color, align: t.align, y: t.y, stroke: t.stroke, strokeColor: t.strokeColor, animation: t.animation, start: t.start })),
    };
    if (engine === "ai_video") {
      if (kind === "none") { toast.error("AI generation needs a source image or video."); setRendering(false); return; }
      try {
        const { job_id } = await api.avGenerate(config, quality);
        setAiStage("Generating with AI… this can take 1–3 minutes");
        let tries = 0;
        const poll = async () => {
          tries += 1;
          const s = await api.avRenderStatus(job_id);
          if (s.status === "ready") { setResult(s); setRendering(false); setAiStage(""); toast.success("AI video generated"); return; }
          if (s.status === "error") { setRendering(false); setAiStage(""); toast.error(s.reason || "AI generation failed"); return; }
          if (tries > 120) { setRendering(false); setAiStage(""); toast.error("AI generation timed out"); return; }
          setTimeout(poll, 3000);
        };
        poll();
      } catch (e) {
        const st = e?.response?.status;
        toast.error(st === 402 ? (e?.response?.data?.detail?.message || "AI generation requires Pro.") : (e?.response?.data?.detail || "Couldn't start AI generation"));
        setRendering(false); setAiStage("");
      }
      return;
    }
    try {
      const r = await api.avRender(config, quality, "manual");
      setResult(r.render);
      toast.success(`Rendered in ${(r.render_ms / 1000).toFixed(1)}s`);
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 402 ? "This needs Pro." : (e?.response?.data?.detail || "Render failed"));
    }
    setRendering(false);
  };

  const reset = () => { setReferenceMedia(null); setSourceMedia(null); setCreativeDirection(""); setTexts([]); setOcr(null); setResult(null); setAlts([]); setStep("start"); };

  if (gate) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white"><Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center gap-3 px-6 py-32 text-center" data-testid="av-gate">
          {gate.reason === "upgrade_required" ? <Zap size={34} className="text-[#7C5CFF]" /> : <Lock size={34} className="text-amber-400" />}
          <h1 className="text-2xl font-black">{gate.reason === "upgrade_required" ? "Animate Video requires Pro" : "Animate Video is unavailable"}</h1>
          <p className="text-white/50">{gate.message}</p>
          {gate.reason === "upgrade_required" && <Link to="/pricing" data-testid="av-get-pro" className="mt-2 rounded-full bg-[#7C5CFF] px-5 py-2.5 text-sm font-bold text-white">Get Pro</Link>}
          <Link to="/tools" className="mt-2 text-sm text-white/50 hover:text-white">← Back to Tools</Link>
        </div>
      </div>
    );
  }
  if (!cfg) return <div className="min-h-screen bg-[#0A0A0A]"><Navbar /><div className="flex justify-center py-40"><Loader2 className="animate-spin text-[#7C5CFF]" /></div></div>;

  const f = cfg.features || {};
  const ratios = cfg.aspect_ratios || [];

  // ---- START SCREEN ----
  if (step === "start") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <input ref={refInput} type="file" accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime,video/webm" hidden onChange={(e) => uploadFile(e.target.files?.[0], "reference")} />
        <div className="mx-auto max-w-[1000px] px-5 py-14 sm:px-6">
          <a href="/tools" className="mb-2 inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"><ArrowLeft size={13} /> Tools</a>
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">{cfg.tool_name || "Animate Video"}</h1>
          <p className="mt-3 max-w-2xl text-base text-white/55">{cfg.description}</p>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {cfg.start?.reference_enabled && (
              <div data-testid="av-start-reference" className="flex flex-col rounded-2xl border border-[#7C5CFF]/30 bg-white/[0.03] p-6">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#7C5CFF] to-[#FF4D8D] text-white"><ImageIcon size={20} /></div>
                <h2 className="text-lg font-bold">Start with Reference</h2>
                <p className="mt-1.5 flex-1 text-[13.5px] leading-relaxed text-white/50">{cfg.start?.reference_helper || "Upload or paste an image/video showing how you want the output to look."}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {f.reference_image && <button data-testid="av-upload-ref-image" onClick={() => { setPending({ role: "reference", type: "image" }); refInput.current.accept = "image/png,image/jpeg,image/webp"; refInput.current?.click(); }} className="inline-flex items-center gap-1.5 rounded-full bg-[#7C5CFF] px-4 py-2 text-xs font-bold text-white"><Upload size={13} /> Upload Image</button>}
                  {f.reference_video && <button data-testid="av-upload-ref-video" onClick={() => { setPending({ role: "reference", type: "video" }); refInput.current.accept = "video/mp4,video/quicktime,video/webm"; refInput.current?.click(); }} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold hover:bg-white/5"><Video size={13} /> Upload Video</button>}
                  {cfg.start?.clipboard_paste && <button data-testid="av-paste-ref" onClick={() => toast.message("Copy an image, then press Ctrl/⌘+V")} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold hover:bg-white/5"><Clipboard size={13} /> Paste</button>}
                </div>
              </div>
            )}
            {cfg.start?.blank_enabled && (
              <div data-testid="av-start-blank" className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white"><Film size={20} /></div>
                <h2 className="text-lg font-bold">Start Blank</h2>
                <p className="mt-1.5 flex-1 text-[13.5px] leading-relaxed text-white/50">Start with an empty canvas and describe exactly what you want to create.</p>
                <button data-testid="av-start-blank-btn" onClick={() => { setStartMode("blank"); setCreativeDirection(""); setStep("workflow"); }} className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full border border-white/20 px-4 py-2 text-xs font-semibold hover:bg-white/5">Start blank <ArrowRight size={13} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- WORKFLOW ----
  const motionAnim = { zoom_in: "ai-zoom-in", zoom_out: "ai-zoom-out", ken_burns: "ai-ken", pulse: "ai-pulse", pan_right: "ai-pan-right", pan_left: "ai-pan-left", none: "" }[cfg.effects?.find((e) => e.effect_id === motion.effect_id)?.motion] || "";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <style>{`@keyframes ai-zoom-in{from{transform:scale(1)}to{transform:scale(1.3)}}@keyframes ai-zoom-out{from{transform:scale(1.3)}to{transform:scale(1)}}@keyframes ai-ken{from{transform:scale(1)}to{transform:scale(1.28) translateX(4%)}}@keyframes ai-pulse{0%,100%{transform:scale(1.04)}50%{transform:scale(1.13)}}@keyframes ai-pan-right{from{transform:scale(1.14) translateX(-5%)}to{transform:scale(1.14) translateX(5%)}}@keyframes ai-pan-left{from{transform:scale(1.14) translateX(5%)}to{transform:scale(1.14) translateX(-5%)}}`}</style>
      <Navbar />
      <input ref={refInput} type="file" hidden onChange={(e) => uploadFile(e.target.files?.[0], "reference")} />
      <input ref={srcInput} type="file" hidden onChange={(e) => uploadFile(e.target.files?.[0], "source")} />
      <div className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <button onClick={reset} className="mb-1 inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"><ArrowLeft size={13} /> Start over</button>
            <h1 className="font-display text-3xl font-black tracking-tight">{cfg.tool_name || "Animate Video"}</h1>
          </div>
          <div className="flex items-center gap-2">
            {f.aspect_ratios && (
              <select data-testid="av-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)} className="rounded-lg border border-white/15 bg-[#111] px-3 py-2 text-sm">
                {ratios.map((r) => <option key={r.id} value={r.id}>{r.id} · {r.label}{r.plan === "pro" ? " (Pro)" : ""}</option>)}
              </select>
            )}
            {cfg.ai?.video_generation_available && (cfg.rendering?.available_engines || []).includes("ai_video") && (
              <div className="flex rounded-lg border border-white/15 p-0.5 text-xs" data-testid="av-engine-toggle">
                <button data-testid="av-engine-native" onClick={() => setEngine("native")} className={`rounded-md px-2.5 py-1.5 font-semibold ${engine === "native" ? "bg-white/15 text-white" : "text-white/50"}`}>Native</button>
                <button data-testid="av-engine-ai" onClick={() => setEngine("ai_video")} className={`rounded-md px-2.5 py-1.5 font-semibold ${engine === "ai_video" ? "bg-gradient-to-r from-[#7C5CFF] to-[#FF4D8D] text-white" : "text-white/50"}`}>✨ AI Video{cfg.ai?.ai_generation_plan === "pro" ? " (Pro)" : ""}</button>
              </div>
            )}
            <button data-testid="av-render-btn" disabled={rendering} onClick={doRender} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C5CFF] to-[#FF4D8D] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-40">
              {rendering ? <><Loader2 size={16} className="animate-spin" /> {engine === "ai_video" ? "Generating…" : "Rendering…"}</> : <><Film size={16} /> {engine === "ai_video" ? "Generate with AI" : "Render"}</>}
            </button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
          {/* CANVAS */}
          <div>
            <div className={`relative mx-auto w-full max-w-[520px] overflow-hidden rounded-2xl border border-white/10 ${RATIO_BOX[aspect]}`} style={{ background: bgColor }} data-testid="av-canvas"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadFile(e.dataTransfer.files?.[0], "source"); }}>
              {result ? (
                <video data-testid="av-result-video" src={`${BE}${result.mp4_url}`} controls autoPlay loop className="h-full w-full bg-black object-contain" />
              ) : sourceMedia?.mediaType === "video" ? (
                <video src={sourceMedia.url} muted autoPlay loop className={`h-full w-full ${videoFit === "contain" ? "object-contain" : "object-cover"}`} />
              ) : sourceMedia?.mediaType === "image" ? (
                <img alt="src" src={sourceMedia.url} className={`h-full w-full ${motion.fit === "contain" ? "object-contain" : "object-cover"}`} style={motionAnim ? { animation: `${motionAnim} ${duration}s ease-in-out infinite alternate` } : {}} />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-white/45">
                  <Film size={28} /><p className="text-sm">Blank canvas — add source media (optional) and describe your video, then Render.</p>
                </div>
              )}
              {!result && texts.map((t) => (
                <div key={t.tid} className="pointer-events-none absolute left-0 right-0 px-4" style={{ top: `${t.y * 100}%`, transform: "translateY(-50%)", textAlign: t.align }}>
                  <span style={{ fontFamily: t.font, fontSize: `${Math.max(14, t.size / 3.2)}px`, color: t.color, fontWeight: 800, WebkitTextStroke: t.stroke ? `1px ${t.strokeColor}` : undefined, textShadow: "2px 2px 6px rgba(0,0,0,.6)" }}>{t.text}</span>
                </div>
              ))}
            </div>
            {referenceMedia && (
              <div className="mx-auto mt-3 flex max-w-[520px] items-center gap-3 rounded-xl border border-[#7C5CFF]/30 bg-[#7C5CFF]/5 p-2.5" data-testid="av-reference-chip">
                {referenceMedia.mediaType === "video" ? <video src={referenceMedia.url} muted className="h-12 w-12 rounded object-cover" /> : <img src={referenceMedia.url} alt="ref" className="h-12 w-12 rounded object-cover" />}
                <div className="flex-1 text-[11px]"><div className="font-bold text-[#b79bff]">Reference · how the output should look</div><div className="text-white/40">Reference only — not the animation source.</div></div>
                {cfg.start?.use_as_source && referenceMedia.mediaType === sourceMedia?.mediaType === false && (
                  <button data-testid="av-use-as-source" onClick={() => setSourceMedia(referenceMedia)} className="rounded-lg border border-white/15 px-2 py-1 text-[10px] font-semibold hover:bg-white/5">Use as Source</button>
                )}
                <button data-testid="av-remove-reference" onClick={() => setReferenceMedia(null)} className="rounded-lg border border-white/15 p-1.5 text-red-400 hover:bg-white/5"><Trash2 size={12} /></button>
              </div>
            )}
            {rendering && engine === "ai_video" && (
              <div className="mx-auto mt-3 flex max-w-[520px] items-center justify-center gap-2 rounded-xl border border-[#7C5CFF]/30 bg-[#7C5CFF]/5 px-4 py-2.5 text-xs text-[#b79bff]" data-testid="av-ai-progress"><Loader2 size={14} className="animate-spin" /> {aiStage || "Generating with AI…"}</div>
            )}
            {result && (
              <div className="mx-auto mt-3 flex max-w-[520px] items-center gap-2" data-testid="av-result-bar">
                <span data-testid="av-result-type" className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${result.render_type === "ai" ? "bg-gradient-to-r from-[#7C5CFF] to-[#FF4D8D] text-white" : "bg-white/10 text-white/70"}`}>{result.render_type === "ai" ? "✨ AI Generated" : "Native Animation"}</span>
                {f.download && <a data-testid="av-download" href={`${BE}${result.mp4_url}`} target="_blank" rel="noreferrer" className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#7C5CFF] to-[#FF4D8D] px-4 py-2.5 text-sm font-bold text-white"><Download size={15} /> Download Video</a>}
                <button data-testid="av-new" onClick={reset} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/5"><RotateCcw size={14} /> Create New</button>
              </div>
            )}
          </div>

          {/* CONTROLS */}
          <div className="space-y-3">
            {/* Reference */}
            <Section title={cfg.start?.reference_label || "How should the output look?"} icon={ImageIcon}>
              <div className="flex flex-wrap gap-2">
                {f.reference_image && <button data-testid="av-ctrl-ref-image" onClick={() => { setPending({ role: "reference", type: "image" }); refInput.current.accept = "image/png,image/jpeg,image/webp"; refInput.current?.click(); }} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/5"><Upload size={13} /> Image</button>}
                {f.reference_video && <button data-testid="av-ctrl-ref-video" onClick={() => { setPending({ role: "reference", type: "video" }); refInput.current.accept = "video/mp4,video/quicktime,video/webm"; refInput.current?.click(); }} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/5"><Video size={13} /> Video</button>}
                {referenceMedia && cfg.features?.ai && <button data-testid="av-reanalyze" disabled={analyzing} onClick={() => analyze(referenceMedia)} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/5">{analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Analyze</button>}
              </div>
            </Section>

            {/* Creative Direction */}
            {f.creative_direction && (
              <Section title="Creative Direction" icon={Sparkles}>
                {analyzing ? <div className="flex items-center gap-2 py-2 text-xs text-white/50"><Loader2 size={14} className="animate-spin" /> Analyzing reference…</div> : null}
                <textarea data-testid="av-prompt" value={creativeDirection} onChange={(e) => setCreativeDirection(e.target.value)} rows={4} placeholder="Describe the video you want to create…" className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs outline-none focus:border-[#7C5CFF]/50" />
                {f.ai_assistant && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="av-assistant-actions">
                    {(cfg.assistant_actions || []).map((a) => {
                      const Icon = ASSIST_ICON[a.id] || Wand2;
                      return <button key={a.id} data-testid={`av-assist-${a.id}`} disabled={aiBusy} onClick={() => runAssistant(a.id)} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:border-[#7C5CFF]/50 hover:text-white disabled:opacity-40">{aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />} {a.label}</button>;
                    })}
                  </div>
                )}
                {alts.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid="av-alternatives">
                    {alts.map((alt, i) => (
                      <button key={i} onClick={() => { setCreativeDirection(alt); setAlts([]); }} className="w-full rounded-lg border border-white/10 bg-white/[0.02] p-2 text-left text-[11px] text-white/70 hover:border-[#7C5CFF]/40">{alt}</button>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Source Media */}
            <Section title="Source Media (optional)" icon={Video}>
              <p className="mb-2 text-[10px] text-white/40">What should be animated? Separate from the reference above.</p>
              <div className="flex flex-wrap gap-2">
                {f.source_image && <button data-testid="av-src-image" onClick={() => { setPending({ role: "source", type: "image" }); srcInput.current.accept = "image/png,image/jpeg,image/webp"; srcInput.current?.click(); }} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/5"><ImageIcon size={13} /> Image</button>}
                {f.source_video && <button data-testid="av-src-video" onClick={() => { setPending({ role: "source", type: "video" }); srcInput.current.accept = "video/mp4,video/quicktime,video/webm"; srcInput.current?.click(); }} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/5"><Video size={13} /> Video</button>}
                {sourceMedia && <button data-testid="av-src-remove" onClick={() => setSourceMedia(null)} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-red-400 hover:bg-white/5"><Trash2 size={13} /> Remove</button>}
              </div>
              {sourceMedia?.mediaType === "image" && cfg.effects && (
                <div className="mt-3 space-y-2">
                  <select data-testid="av-effect" value={motion.effect_id} onChange={(e) => setMotion((m) => ({ ...m, effect_id: e.target.value }))} className="w-full rounded-lg border border-white/15 bg-[#111] px-2 py-2 text-xs">{cfg.effects.map((e) => <option key={e.effect_id} value={e.effect_id}>{e.name}</option>)}</select>
                  <div className="flex items-center gap-2 text-[10px] text-white/50"><span>Intensity</span><input type="range" min={5} max={100} value={motion.intensity} onChange={(e) => setMotion((m) => ({ ...m, intensity: +e.target.value }))} className="flex-1" /></div>
                </div>
              )}
              {sourceMedia?.mediaType === "video" && (
                <div className="mt-3">
                  <label className="text-[10px] text-white/50">Reframe to canvas</label>
                  <select data-testid="av-video-fit" value={videoFit} onChange={(e) => setVideoFit(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-[#111] px-2 py-2 text-xs">{["cover", "contain"].map((d) => <option key={d} value={d}>{d}</option>)}</select>
                </div>
              )}
            </Section>

            {/* Canvas / duration / bg */}
            <Section title="Canvas" icon={Film}>
              <div className="flex items-center gap-2"><span className="text-xs text-white/50">Background</span><input data-testid="av-bg-color" type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="h-8 w-14 rounded border border-white/15 bg-transparent" /></div>
              <div className="mt-2 flex items-center gap-2 text-[10px] text-white/50"><span>Duration {duration}s</span><input data-testid="av-duration" type="range" min={2} max={cfg.limits?.max_duration || 30} value={duration} onChange={(e) => setDuration(+e.target.value)} className="flex-1" /></div>
              <div className="mt-2"><label className="text-[10px] text-white/50">Quality</label><select data-testid="av-quality" value={quality} onChange={(e) => setQuality(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-[#111] px-2 py-2 text-xs">{(cfg.rendering?.quality_options || ["low", "medium", "high"]).map((q) => <option key={q} value={q}>{q}</option>)}</select></div>
            </Section>

            {/* Text */}
            {f.text && (
              <Section title="Text" icon={Type}>
                <div className="flex gap-2">
                  <button data-testid="av-add-text" onClick={() => setTexts((t) => [...t, newText({ font: cfg.text?.default_font, size: cfg.text?.default_size })])} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/15 py-2 text-xs font-semibold hover:bg-white/5"><Plus size={13} /> Add Text</button>
                  {f.ocr && <button data-testid="av-ocr-btn" onClick={runOcr} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/5"><ScanText size={13} /> Detect</button>}
                </div>
                {ocr && ocr.map((b) => (
                  <div key={b.id} className="mt-2 flex items-center gap-2" data-testid={`av-ocr-block-${b.id}`}>
                    <input value={b.text} onChange={(e) => setOcr((o) => o.map((x) => x.id === b.id ? { ...x, text: e.target.value } : x))} className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs" />
                    <button onClick={() => setTexts((t) => [...t, newText({ text: b.text })])} className="rounded bg-white/10 px-2 py-1 text-[10px] font-bold hover:bg-white/20">+ Text</button>
                  </div>
                ))}
                {texts.map((t) => (
                  <div key={t.tid} className="mt-2 space-y-1.5 rounded-lg border border-white/10 bg-white/[0.02] p-2" data-testid={`av-text-${t.tid}`}>
                    <div className="flex items-center gap-2">
                      <input data-testid={`av-text-input-${t.tid}`} value={t.text} onChange={(e) => updText(t.tid, { text: e.target.value })} className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs" />
                      <button data-testid={`av-text-del-${t.tid}`} onClick={() => delText(t.tid)} className="rounded border border-white/15 p-1.5 text-red-400 hover:bg-white/5"><Trash2 size={12} /></button>
                    </div>
                    <div className="flex gap-1.5">
                      <select value={t.font} onChange={(e) => updText(t.tid, { font: e.target.value })} className="min-w-0 flex-1 rounded border border-white/15 bg-[#111] px-1.5 py-1 text-[11px]">{(cfg.fonts || []).map((ff) => <option key={ff} value={ff}>{ff}</option>)}</select>
                      <select value={t.animation} onChange={(e) => updText(t.tid, { animation: e.target.value })} className="rounded border border-white/15 bg-[#111] px-1.5 py-1 text-[11px]">{(cfg.text_animations || []).map((a) => <option key={a} value={a}>{a}</option>)}</select>
                      <input type="color" value={t.color} onChange={(e) => updText(t.tid, { color: e.target.value })} className="h-7 w-8 rounded border border-white/15 bg-transparent" />
                    </div>
                  </div>
                ))}
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
    <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white/60"><Icon size={13} /> {title}</div>
    {children}
  </div>
);
